import logger from '../utils/logger';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Tag, ArrowRight, Filter, Images } from 'lucide-react';
import axios from 'axios';
import PhotoLightbox from '../components/PhotoLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['All', 'Fundraisers', 'Community Support', 'Health Initiatives'];

const CATEGORY_COLORS = {
  'Fundraisers': 'bg-amber-100 text-amber-800',
  'Community Support': 'bg-blue-100 text-blue-800',
  'Health Initiatives': 'bg-emerald-100 text-emerald-800',
};

const Updates = () => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  // Shared PhotoLightbox state: which update is open + which photo index.
  // Photos are computed lazily so we never touch the update objects.
  const [lightbox, setLightbox] = useState(null);   // { update, startIndex } | null
  const lightboxPhotos = useMemo(() => {
    if (!lightbox) return [];
    const u = lightbox.update || {};
    const arr = [];
    if (u.image_url) arr.push(u.image_url);
    if (Array.isArray(u.additional_photos)) {
      for (const p of u.additional_photos) {
        if (p && !arr.includes(p)) arr.push(p);
      }
    }
    return arr;
  }, [lightbox]);
  const openLightbox = (update, startIndex = 0) => setLightbox({ update, startIndex });
  const closeLightbox = () => setLightbox(null);

  const fetchUpdates = useCallback(async () => {
    setLoading(true);
    try {
      const params = activeCategory !== 'All' ? `?category=${encodeURIComponent(activeCategory)}` : '';
      const response = await axios.get(`${BACKEND_URL}/api/public/updates${params}`);
      setUpdates(response.data);
    } catch (error) {
      logger.error('Error fetching updates:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => {
    fetchUpdates();
  }, [fetchUpdates]);

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'Coming Soon') return 'Coming Soon';
    const d = new Date(dateStr + 'T00:00:00');
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div>
      {/* Hero */}
      <section className="bg-[#0A0A0A] py-12" data-testid="updates-hero">
        <div className="px-6 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-2">
            Stay Informed
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-3">
            Community Updates
          </h1>
          <p className="text-gray-400 text-sm max-w-md mx-auto">
            We believe in sharing real impact — not assumptions. Updates will be posted here as distributions, events, and community work take place.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-b border-[#E5E7EB] sticky top-14 md:top-16 z-10" data-testid="updates-filter">
        <div className="px-6 py-3 flex items-center gap-2 overflow-x-auto">
          <Filter className="w-4 h-4 text-[#4B5563] flex-shrink-0" />
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                activeCategory === cat
                  ? 'bg-[#BA1826] text-white'
                  : 'bg-[#F8F9FA] text-[#4B5563] hover:bg-[#E5E7EB]'
              }`}
              data-testid={`filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Updates Feed */}
      <section className="py-8 bg-white" data-testid="updates-feed">
        <div className="px-6">
          {/* Loading → Empty → Content */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#BA1826] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : updates.length === 0 ? (
            <div className="text-center py-12" data-testid="no-updates">
              <p className="text-[#4B5563] text-sm">No updates found for this category.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {updates.map((update, index) => {
                const extras = Array.isArray(update.additional_photos)
                  ? update.additional_photos.filter(Boolean) : [];
                const totalPhotos = (update.image_url ? 1 : 0) + extras.length;
                return (
                <article
                  key={update.id}
                  className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden card-hover"
                  data-testid={`update-card-${update.id}`}
                >
                  {update.image_url && (
                    <button type="button"
                      onClick={() => openLightbox(update, 0)}
                      className="block w-full aspect-[16/7] overflow-hidden relative group focus:outline-none focus:ring-2 focus:ring-[#BA1826]"
                      aria-label={totalPhotos > 1
                        ? `View ${update.title} photo story (${totalPhotos} photos)`
                        : `View ${update.title} photo`}
                      data-testid={`update-featured-open-${update.id}`}>
                      <img
                        src={update.image_url}
                        alt={update.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                      />
                      {totalPhotos > 1 && (
                        <span className="absolute bottom-2 right-2 bg-black/70 text-white rounded-full text-[11px] font-semibold px-2.5 py-1 flex items-center gap-1 shadow"
                              data-testid={`update-photo-badge-${update.id}`}>
                          <Images className="w-3 h-3" /> +{totalPhotos - 1} photos
                        </span>
                      )}
                    </button>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${CATEGORY_COLORS[update.category] || 'bg-gray-100 text-gray-700'}`}>
                        {update.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-[#4B5563]">
                        <Calendar className="w-3 h-3" />
                        {formatDate(update.date)}
                      </span>
                    </div>
                    <h2 className="font-heading text-lg font-bold text-[#0A0A0A] mb-2 leading-snug">
                      {update.title}
                    </h2>
                    <p className="text-sm text-[#4B5563] leading-relaxed">
                      {update.description}
                    </p>
                  </div>
                </article>
              );})}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 bg-[#F8F9FA]" data-testid="updates-cta">
        <div className="px-6 text-center">
          <h3 className="font-heading text-xl font-bold text-[#0A0A0A] mb-2">
            Want to Be Part of the Story?
          </h3>
          <p className="text-sm text-[#4B5563] mb-4 max-w-sm mx-auto">
            Join our mission and help us create more impact stories across the globe.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/donate" className="btn-primary text-sm" data-testid="updates-donate-btn">
              Donate Now <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/involved" className="btn-secondary text-sm" data-testid="updates-involved-btn">
              Get Involved
            </Link>
          </div>
        </div>
      </section>

      {/* Shared PhotoLightbox — one instance for the whole page. */}
      <PhotoLightbox
        open={!!lightbox}
        photos={lightboxPhotos}
        startIndex={lightbox ? lightbox.startIndex : 0}
        title={lightbox ? (lightbox.update.title || 'Community Update') : ''}
        onClose={closeLightbox}
      />
    </div>
  );
};

export default Updates;
